/**
 * "Take me back where I was going" across sign-in.
 *
 * Someone opens a deep link (e.g. the event in a "new event" email) while
 * signed out: ProtectedRoute sends them to /login with the page in router
 * state, LoginPage remembers it here, and after the magic link signs them in
 * they land on that page instead of the default route.
 *
 * localStorage, not router state, because the magic link opens in a new tab
 * where router state does not exist. Same browser only — a link opened on
 * another device falls back to the normal landing route. Kept for a day so a
 * stale intention never hijacks a later, unrelated sign-in.
 */

const KEY = "post_login_redirect";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Only same-site paths: "/events/abc", never "//evil.com" or a full URL. */
const isSafePath = (path: string) =>
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.startsWith("/login") &&
  !path.startsWith("/signup");

export function rememberReturnPath(path: string | null | undefined): void {
  if (!path || !isSafePath(path)) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Storage unavailable (private mode): the default landing route is fine.
  }
}

/** The remembered path, once — reading it clears it. */
export function takeReturnPath(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    localStorage.removeItem(KEY);
    if (!raw) return null;
    const { path, at } = JSON.parse(raw) as { path?: string; at?: number };
    if (!path || !isSafePath(path) || !at || Date.now() - at > MAX_AGE_MS) return null;
    return path;
  } catch {
    return null;
  }
}
